import { Download } from "lucide-react";
import {
  Container,
  Section,
  Heading,
  Lede,
  Eyebrow,
  Card,
} from "../components/ui";
import { useSeo } from "../lib/seo";

const ASSETS = [
  { label: "Horizontal lockup", file: "/brand/logo-horizontal.svg" },
  { label: "Horizontal — no tagline", file: "/brand/logo-horizontal-no-tagline.svg" },
  { label: "Icon (squircle)", file: "/brand/logo-icon.svg" },
  { label: "Wordmark only", file: "/brand/logo-wordmark-only.svg" },
  { label: "Stacked", file: "/brand/logo-stacked.svg" },
  { label: "Mono — black", file: "/brand/logo-mono-black.svg" },
  { label: "Mono — white", file: "/brand/logo-mono-white.svg" },
  { label: "Favicon", file: "/brand/favicon.svg" },
];

export function Press() {
  useSeo({
    title: "Press kit — WeConsult Rx",
    description:
      "WeConsult Rx press kit: logo lockups, brand color palette, brand guidelines, and founder bios.",
    path: "/press",
  });
  return (
    <>
      <Section>
        <Container>
          <div className="max-w-3xl">
            <Eyebrow>Press</Eyebrow>
            <Heading level={1} className="mt-3">
              Press kit
            </Heading>
            <Lede className="mt-6">
              Logo lockups, color palette, and brand guidelines for media,
              partners, and integrations.
            </Lede>
          </div>
        </Container>
      </Section>

      <Section className="pt-0">
        <Container>
          <Heading level={2}>Logo lockups</Heading>
          <p className="mt-3 text-ink-600">
            All assets ship as SVG. Right-click to download.
          </p>
          <div className="mt-8 grid gap-6 md:grid-cols-2 lg:grid-cols-4">
            {ASSETS.map((a) => (
              <Card key={a.file}>
                <div className="flex items-center justify-center h-28 bg-canvas rounded-lg border border-border">
                  <img src={a.file} alt={a.label} className="max-h-20 max-w-full" />
                </div>
                <div className="mt-4 flex items-center justify-between gap-3">
                  <p className="text-sm font-semibold text-ink-900">{a.label}</p>
                  <a
                    href={a.file}
                    download
                    className="inline-flex items-center gap-1 text-sm text-forest-700 hover:text-forest-900"
                  >
                    <Download className="h-4 w-4" aria-hidden />
                    SVG
                  </a>
                </div>
              </Card>
            ))}
          </div>
        </Container>
      </Section>

      <Section tone="soft">
        <Container>
          <Heading level={2}>Color palette</Heading>
          <div className="mt-8 grid gap-4 md:grid-cols-3 lg:grid-cols-6">
            {[
              ["Forest 700", "#15803D"],
              ["Forest 900", "#14532D"],
              ["Ink 900", "#0F172A"],
              ["Slate 500", "#64748B"],
              ["Stone 50", "#FAFAF9"],
              ["White", "#FFFFFF"],
            ].map(([name, hex]) => (
              <Card key={hex} className="text-center">
                <div
                  className="h-20 rounded-lg border border-border"
                  style={{ backgroundColor: hex }}
                />
                <p className="mt-3 font-semibold text-ink-900">{name}</p>
                <p className="mt-1 text-sm text-ink-500 font-mono">{hex}</p>
              </Card>
            ))}
          </div>

          <div className="mt-12 grid gap-8 md:grid-cols-2">
            <div>
              <Heading level={3}>Brand name</Heading>
              <p className="mt-3 text-ink-700">
                Always written “<strong>WeConsult Rx</strong>” with a single
                space and lowercase x in Rx. Never WECONSULTRX, WeConsultRX, or
                we consult rx.
              </p>
            </div>
            <div>
              <Heading level={3}>Tagline</Heading>
              <p className="mt-3 text-ink-700">
                <span className="tagline">Clinical · Compliance · AI</span>
              </p>
            </div>
          </div>
        </Container>
      </Section>
    </>
  );
}
